import { createContext, useContext } from 'react'

/**
 * The app-wide notice: one-shot confirmations ("Project updated") and request
 * failures ("Failed to delete task") raised from anywhere, shown by the one
 * surface `NoticeProvider` mounts near the app root.
 *
 * Pages call `notify.success(msg)` / `notify.error(msg)` instead of owning
 * `error` / `success` state just to show a toast. Each call is a new notice,
 * even with the same text as the one on screen, so a repeated failure shows
 * again rather than being swallowed as "no change".
 *
 * Not for inline validation that belongs next to a form field, or for state
 * the user has to act on (the quota banner, "updates available"): those stay
 * where they are.
 */
export type NoticeKind = 'success' | 'error'

// How long a notice stays up. Errors stay longer: they are the ones worth
// reading twice.
export const NOTICE_DURATION_MS: Record<NoticeKind, number> = {
  success: 4000,
  error: 7000,
}

export interface Notify {
  success: (message: string) => void
  error: (message: string) => void
}

// The default is a no-op so a component rendered without the provider — a
// unit test mounting one page, say — raises into nothing instead of throwing.
// The app itself always has the provider (App.tsx).
export const NoticeContext = createContext<Notify>({
  success: () => {},
  error: () => {},
})

export const useNotify = (): Notify => useContext(NoticeContext)
